<template>
    <div ref="questionsContainer">
        <p v-if="disabled && !editing && !endOfQuestionsDisplayed" variant="primary" class="text-muted">
            * Additional questions may be asked depending on answers.
        </p>

        <component :is="editing ? 'Draggable' : 'div'" v-model="localQuestions" :draggable="editing ? '.item' : undefined"
            handle=".handle" @update="movedQuestion" :disabled="disabled || !editing">
            <TransitionGroup name="fade">
                <div v-for="(q, i) in questionsToDisplay" class="item" :key="q.id">
                    <div v-if="!disabled && editing" class="insert-question-zone">
                        <b-button variant="outline-primary" size="sm" @click="insertQuestionAt(i)" class="insert-btn" v-b-tooltip.hover title="Insert question here">
                            <i class="fas fa-plus"></i>
                        </b-button>
                    </div>
                    <card plain>
                    <div class="d-flex">
                        <div class="handle d-flex flex-column align-items-center">
                            <i v-if="!disabled && editing" class="fas fa-grip-horizontal" />
                            <b-badge variant="primary">
                                {{ editing ? q.position : i + 1 }}
                            </b-badge>

                            <!-- {{ q.id }} -->
                        </div>
                        <div class="flex-fill">
                            <TaskQuestion :question="q" :questions="localQuestions" @remove="removeQuestion(i)"
                                @update-question="updateQuestion(i, $event)"
                                :disabled="disabled" :editing="editing" :answers="answers" :show-errors="showErrors"
                                @input="$emit('question-input')" :collapse-all="collapseAll && !hasValidationErrors(q)"
                                :entity-type="entityType" />
                        </div>
                    </div>
                    </card>
                </div>
            </TransitionGroup>
        </component>

        <b-row v-if="editing" class="align-items-center p-2 edit-actions glass-pane">
            <b-col>
                <template v-if="localQuestions.length">
                    {{ localQuestions.length }} {{ localQuestions.length > 1 ? "questions" : "question" }} total
                </template>
            </b-col>
            <b-col class="text-center">
                <b-button v-if="!disabled && editing" variant="primary" @click="addQuestion" size="sm">
                    Add Question
                </b-button>
            </b-col>
            <b-col class="text-right">
                <b-button v-if="localQuestions.length" variant="secondary" size="sm" @click="collapseAll = !collapseAll">
                    <i :class="{ 'fas fa-angle-double-up': true, 'fa-rotate-180': collapseAll }"
                        style="transition: all .3s;"></i>
                    {{ collapseAll ? "Edit All" : "Collapse All" }}
                </b-button>
            </b-col>
        </b-row>

        <div class="text-center">
            <h5 v-if="disabled && !localQuestions?.length" class="text-muted">
                No questions
            </h5>

            <b-badge v-if="!disabled && !editing && endOfQuestionsDisplayed" variant="info">
                End of Questions
            </b-badge>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import TaskQuestion from './TaskQuestion.vue';
import Draggable from 'vuedraggable';
import { UserTaskAnswer, UserTaskQuestion, UserTaskQuestionOption, UserTaskQuestionType } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';

@Component({
    components: {
        TaskQuestion,
        Draggable
    }
})
export default class TaskQuestions extends Vue {

    @Prop({ required: true }) questions!: UserTaskQuestion[];
    @Prop({ default: null }) answers!: UserTaskAnswer[] | null;
    @Prop({ default: false }) disabled!: boolean;
    @Prop({ default: false }) editing!: boolean;
    @Prop({ default: false }) showErrors!: boolean;
    @Prop({ default: EntityType.UserTask }) entityType!: EntityType;

    @Ref("questionsContainer") questionsContainer!: HTMLDivElement;

    private collapseAll = false;
    private localQuestions: UserTaskQuestion[] = [];

    mounted() {
        this.localQuestions = [...this.questions];
    }

    @Watch('questions', { immediate: true })
    onQuestionsChange(newQuestions: UserTaskQuestion[]) {
        this.localQuestions = [...newQuestions];
    }

    emitQuestionsChange() {
        this.$emit('input', this.localQuestions);
    }

    movedQuestion() {
        // Build old-position -> new-position mapping before renumbering
        const positionMap = new Map<number, number>();
        this.localQuestions.forEach((q, i) => {
            positionMap.set(q.position, i + 1);
        });

        // Update positions to match new array order
        this.localQuestions.forEach((q, i) => {
            q.position = i + 1;
        });

        // Remap all nextQuestionPosition references
        this.localQuestions.forEach((q) => {
            if (q.nextQuestionPosition > 0 && positionMap.has(q.nextQuestionPosition)) {
                q.nextQuestionPosition = positionMap.get(q.nextQuestionPosition)!;
            }
            q.options.forEach((o: any) => {
                if (o.nextQuestionPosition > 0 && positionMap.has(o.nextQuestionPosition)) {
                    o.nextQuestionPosition = positionMap.get(o.nextQuestionPosition)!;
                }
            });
        });

        this.localQuestions = structuredClone(this.localQuestions);
        this.emitQuestionsChange();
    }

    addQuestion() {
        const newQuestionPosition = this.localQuestions.length + 1;
        const newQuestion = {
            id: -(new Date().getTime() % 2147483647), // Negative int32 for temp IDs
            typeId: UserTaskQuestionType.multipleChoiceSingle,
            name: "",
            text: "",
            helpText: "",
            position: newQuestionPosition,
            nextQuestionPosition: -1,
            options: [
                {
                    text: "",
                    nextQuestionPosition: -1
                }
            ],
            required: false,
            internal: false
        };

        //Update previous final question's next question
        if (this.localQuestions.length > 1) {
            const penultimateQuestion = this.localQuestions[this.localQuestions.length - 2];
            penultimateQuestion.nextQuestionPosition = newQuestionPosition;
            penultimateQuestion.options.forEach((o: any) => {
                o.nextQuestionPosition = newQuestionPosition;
            })
        }

        this.localQuestions.push(newQuestion);
        // Use structuredClone to ensure reactivity
        this.localQuestions = structuredClone(this.localQuestions);
        this.emitQuestionsChange();
    }

    insertQuestionAt(index: number) {
        const insertPosition = index + 1; // Position is 1-based, index is 0-based

        // Create new question with position at insertion point
        const newQuestion = {
            id: -(new Date().getTime() % 2147483647), // Negative int32 for temp IDs
            typeId: UserTaskQuestionType.multipleChoiceSingle,
            name: "",
            text: "",
            helpText: "",
            position: insertPosition,
            nextQuestionPosition: -1,
            options: [
                {
                    text: "",
                    nextQuestionPosition: -1
                }
            ],
            required: false,
            internal: false
        };

        // Update all positions for questions at or after the insertion point
        this.localQuestions.forEach((q) => {
            if (q.position >= insertPosition) {
                q.position += 1;
            }
        });

        // Update all nextQuestionPosition references that point to positions >= insertPosition
        this.localQuestions.forEach((q) => {
            // Update question's nextQuestionPosition
            if (q.nextQuestionPosition >= insertPosition) {
                q.nextQuestionPosition += 1;
            }

            // Update option nextQuestionPosition values
            q.options.forEach((o: any) => {
                if (o.nextQuestionPosition >= insertPosition) {
                    o.nextQuestionPosition += 1;
                }
            });
        });

        // Determine what the new question should point to
        if (index < this.localQuestions.length) {
            // Point to the question that was previously at this position (now shifted by 1)
            newQuestion.nextQuestionPosition = insertPosition + 1;
            newQuestion.options[0].nextQuestionPosition = insertPosition + 1;
        } else {
            // Inserting at the end (shouldn't happen with current UI but handle it)
            newQuestion.nextQuestionPosition = -1;
            newQuestion.options[0].nextQuestionPosition = -1;
        }

        // Update the previous question to point to the new question
        if (index > 0) {
            const previousQuestion = this.localQuestions[index - 1];
            previousQuestion.nextQuestionPosition = insertPosition;
            previousQuestion.options.forEach((o: any) => {
                o.nextQuestionPosition = insertPosition;
            });
        }

        // Insert the new question at the correct array position
        this.localQuestions.splice(index, 0, newQuestion);

        // Use structuredClone to ensure reactivity
        this.localQuestions = structuredClone(this.localQuestions);
        this.emitQuestionsChange();
    }

    updateQuestion(index: number, updatedQuestion: any) {
        this.$set(this.localQuestions, index, updatedQuestion);
        this.emitQuestionsChange();
    }

    removeQuestion(index: number) {
        const removedPosition = this.localQuestions[index].position;
        this.localQuestions.splice(index, 1);

        // Renumber positions
        this.localQuestions.forEach((q, i) => q.position = i + 1);

        // Update nextQuestionPosition references
        this.localQuestions.forEach((q) => {
            if (q.nextQuestionPosition === removedPosition) {
                q.nextQuestionPosition = -1;
            } else if (q.nextQuestionPosition > removedPosition) {
                q.nextQuestionPosition -= 1;
            }
            q.options.forEach((o: any) => {
                if (o.nextQuestionPosition === removedPosition) {
                    o.nextQuestionPosition = -1;
                } else if (o.nextQuestionPosition > removedPosition) {
                    o.nextQuestionPosition -= 1;
                }
            });
        });

        this.localQuestions = structuredClone(this.localQuestions);
        this.emitQuestionsChange();
    }

    get questionsToDisplay() {
        if (this.editing) return this.localQuestions;

        const displayQuestions: UserTaskQuestion[] = [];

        let nextQuestionPosition = 1;

        this.localQuestions?.every(q => {
            if (nextQuestionPosition < 0) return false;
            if (q.position < nextQuestionPosition) return true;

            displayQuestions.push(q);

            //Only multiple choice single questions support branching
            if (q.typeId !== UserTaskQuestionType.multipleChoiceSingle) {
                nextQuestionPosition = q.nextQuestionPosition ?? -1;
                return true;
            }

            if (!this.questionIsMultipleBranch(q)) {
                nextQuestionPosition = q.options[0].nextQuestionPosition ?? -1;
                return true;
            }

            const answer = this.answers?.find(a => a.taskQuestionId == q.id);
            if (!answer) return false;

            const option = q.options.find(o => o.text === answer.answer);
            if (!option) return false;
            nextQuestionPosition = option.nextQuestionPosition ?? -1;

            return true;
        });

        return displayQuestions;
    }

    get lastQuestionDisplayed() {
        return this.questionsToDisplay.at(-1);
    }

    get endOfQuestionsDisplayed() {
        const lastQuestion = this.lastQuestionDisplayed;
        if (lastQuestion?.typeId == UserTaskQuestionType.multipleChoiceSingle) {
            const answer = this.answers?.find(a => a.taskQuestionId == lastQuestion?.id);
            if (!this.questionIsMultipleBranch(lastQuestion) &&
                lastQuestion.options.length &&
                (lastQuestion.options[0].nextQuestionPosition ?? -1) < 0)
                return true;
            if (!answer) return false;
            const option = lastQuestion.options.find((o: UserTaskQuestionOption) => o.text === answer.answer);
            return !option || ((option.nextQuestionPosition ?? -1) < 0);
        }
        return (this.lastQuestionDisplayed?.nextQuestionPosition ?? -1) < 0;
    }

    questionIsMultipleBranch(question: UserTaskQuestion) {
        if (question.typeId !== UserTaskQuestionType.multipleChoiceSingle) return false;
        return !question.options.every(o => o.nextQuestionPosition === question.options[0].nextQuestionPosition);
    }

    cleanAnswers() {
        return this.answers?.filter(a => this.questionsToDisplay.find(q => q.id == a.taskQuestionId)) ?? [];
    }

    public answersHaveErrors() {
        return this.questionsToDisplay.some(q => {
            if (q.typeId === UserTaskQuestionType.textCopy) return false;
            if ((!this.questionIsMultipleBranch(q) && !q.required)) return false;
            const answer = this.answers?.find(a => a.taskQuestionId == q.id);
            return !answer?.answer;
        });
    }

    hasQuestions() {
        return !!this.questionsToDisplay.length;
    }

    hasValidationErrors(question: UserTaskQuestion) {
        if (!this.showErrors || !this.editing) return false;

        // Check if question text is empty
        if (!question.text?.trim()) return true;

        // Check multiple choice questions for empty or duplicate options
        if ([UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(question.typeId)) {
            if (question.options.some(o => !o.text?.trim())) return true;
            const uniqueTexts = new Set(question.options.map(o => o.text?.trim()));
            if (uniqueTexts.size !== question.options.length) return true;
        }

        return false;
    }

    @Watch("showErrors")
    handleShowErrorsChange() {
        if (this.showErrors) {
            this.$nextTick(() => {
                const firstError = this.questionsContainer.querySelector(".alert");
                firstError?.closest(".card")?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }

}

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.edit-actions {
    position: sticky;
    bottom: 0;
    z-index: 150;
}

::v-deep(.card) {
    background: white;
}

::v-deep(.d-flex) {
    gap: 10px;
}

::v-deep(.custom-radio .custom-control-input:disabled:checked ~ .custom-control-label::before),
::v-deep(.custom-checkbox .custom-control-input:disabled:checked ~ .custom-control-label::before) {
    background-color: #007bff;
}

::v-deep(.custom-radio .custom-control-input:disabled:checked ~ .custom-control-label),
::v-deep(.custom-checkbox .custom-control-input:disabled:checked ~ .custom-control-label) {
    color: #3A3A3A;
    font-weight: bold;
}

::v-deep(.form-control[disabled]) {
    background: white;
    color: #3A3A3A;
}

.insert-question-zone {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    margin: -16px auto 0;
    width: 100%;
}

.insert-btn {
    border-style: dashed;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.2s, border-style 0.2s;
}

.insert-btn:hover {
    opacity: 1;
    border-style: solid;
}
</style>
